import type { ReactNode } from 'react';
import { useLanguage } from '../../shell/language-context';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { highlightMatch } from './highlight-match';

export type ServiceTab = 'all' | 'cats' | 'biz' | 'pros';

// One pickable row of the service panel. The pill builds the list (so its keyboard
// handler walks exactly what is on screen) and this component draws it.
export interface ServiceRow {
  key: string;
  kind: 'cat' | 'biz' | 'pro';
  label: string;
  sub?: string;
  glyph?: string;
  // SVG path (24-px box) drawn in place of the text glyph when set.
  icon?: string;
  tint?: string;
  glyphColor?: string;
  image?: string;
  onPick: () => void;
}

function Hit({ label, query }: { label: string; query: string }) {
  const { pre, hit, post } = highlightMatch(label, query);
  return (
    <>
      {pre}
      {hit ? <span className="hm-panel__hit">{hit}</span> : null}
      {post}
    </>
  );
}

// Hero panel 1 (design DC:306-357): tabs All / Categories / Businesses /
// Professionals with live counts, then the matching rows with the query run
// highlighted. Keyboard selection (`active`) is driven from the segment's input.
export function HeroServicePanel({
  query,
  tab,
  onTab,
  counts,
  rows,
  active,
  listId,
}: {
  query: string;
  tab: ServiceTab;
  onTab: (tab: ServiceTab) => void;
  counts: { cats: number; biz: number; pros: number };
  rows: ServiceRow[];
  active: number;
  listId: string;
}) {
  const { t, lang } = useLanguage();
  const n = (v: number) => formatNumber(lang, v);
  const tabs: { id: ServiceTab; label: string }[] = [
    { id: 'all', label: t('dc50.svc.tab_all') },
    { id: 'cats', label: t('dc50.svc.tab_count', { label: t('dc50.svc.cats'), n: n(counts.cats) }) },
    { id: 'biz', label: t('dc50.svc.tab_count', { label: t('dc50.svc.biz'), n: n(counts.biz) }) },
    { id: 'pros', label: t('dc50.svc.tab_count', { label: t('dc50.svc.pros'), n: n(counts.pros) }) },
  ];

  const groups: { kind: ServiceRow['kind']; head: string; first: boolean }[] = [
    { kind: 'cat', head: t('dc50.svc.cats'), first: true },
    { kind: 'biz', head: t('dc50.svc.biz'), first: false },
    { kind: 'pro', head: t('dc50.svc.pros'), first: false },
  ];

  let index = -1;
  const body: ReactNode[] = [];
  for (const g of groups) {
    const list = rows.filter((r) => r.kind === g.kind);
    if (list.length === 0) continue;
    body.push(
      <div key={`h-${g.kind}`} className={g.first ? 'hm-panel__head' : 'hm-panel__head hm-panel__head--gap'}>
        {g.head}
      </div>,
    );
    for (const r of list) {
      index += 1;
      const on = index === active;
      body.push(
        <button
          key={r.key}
          type="button"
          role="option"
          id={`${listId}-${index}`}
          aria-selected={on}
          className={on ? 'hm-panel__row is-kb' : 'hm-panel__row'}
          onMouseDown={(e) => e.preventDefault()}
          onClick={r.onPick}
        >
          {r.kind === 'cat' ? (
            <span className="hm-panel__glyph" style={{ background: r.tint, color: r.glyphColor }} aria-hidden="true">
              {r.icon ? (
                <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                  <path d={r.icon} />
                </svg>
              ) : (
                r.glyph
              )}
            </span>
          ) : r.image ? (
            <img
              className={r.kind === 'pro' ? 'hm-panel__thumb hm-panel__thumb--round' : 'hm-panel__thumb'}
              src={r.image}
              alt=""
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className={r.kind === 'pro' ? 'hm-panel__thumb hm-panel__thumb--round' : 'hm-panel__thumb'} aria-hidden="true" />
          )}
          {r.kind === 'cat' ? (
            <span className="hm-panel__label">
              <Hit label={r.label} query={query} />
            </span>
          ) : (
            <span className="hm-panel__txt">
              <span className={r.kind === 'biz' ? 'hm-panel__label hm-panel__label--clip' : 'hm-panel__label'}>
                <Hit label={r.label} query={query} />
              </span>
              {r.sub ? <span className="hm-panel__sub">{r.sub}</span> : null}
            </span>
          )}
        </button>,
      );
    }
  }

  return (
    <div className="hm-panel hm-panel--svc">
      <div className="hm-panel__tabs" role="tablist" aria-label={t('dc50.svc.tabs_label')}>
        {tabs.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={tab === x.id}
            className={tab === x.id ? 'hm-panel__tab is-on' : 'hm-panel__tab'}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onTab(x.id)}
          >
            {x.label}
          </button>
        ))}
      </div>
      <div className="hm-panel__list" role="listbox" id={listId} aria-label={t('dc50.svc.results_label')}>
        {body}
        {query.trim() && rows.length === 0 ? (
          <div className="hm-panel__empty">
            <div className="hm-panel__empty-title">{t('dc50.svc.no_matches', { q: query.trim() })}</div>
            <div className="hm-panel__empty-sub">{t('dc50.svc.try_category')}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
